import type { Fill } from '@vp/shared-types';

/**
 * Pattern fills (lines, grid) of free-form closed paths for the WebGL backend: the pattern lines
 * are cut to the outline (and its holes, even-odd) on the CPU and drawn as thin quads. Same layout
 * as Canvas 2D: lines perpendicular to n = (cos a, sin a) at offsets k·spacing, a grid is two
 * families (vertical and horizontal), all in the path's local coordinates.
 */
const MAX_LINES = 5000;

/** Directions (unit n) of the line families of a fill. */
function families(fill: Extract<Fill, { kind: 'lines' | 'grid' }>): [number, number][] {
  if (fill.kind === 'grid') return [[1, 0], [0, 1]];
  const a = fill.angle ?? Math.PI / 4;
  return [[Math.cos(a), Math.sin(a)]];
}

/** Segments [x0, y0, x1, y1, …] of the pattern lines inside the rings. */
export function patternSegments(rings: number[][], fill: Extract<Fill, { kind: 'lines' | 'grid' }>, spacing: number, view?: { x0: number; y0: number; x1: number; y1: number }): number[] {
  const out: number[] = [];
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const r of rings) {
    for (let i = 0; i < r.length; i += 2) {
      x0 = Math.min(x0, r[i]);
      x1 = Math.max(x1, r[i]);
      y0 = Math.min(y0, r[i + 1]);
      y1 = Math.max(y1, r[i + 1]);
    }
  }
  if (!(x1 > x0) && !(y1 > y0)) return out;
  // only what can be seen: lines outside the view are skipped
  if (view) {
    x0 = Math.max(x0, view.x0);
    y0 = Math.max(y0, view.y0);
    x1 = Math.min(x1, view.x1);
    y1 = Math.min(y1, view.y1);
    if (x1 < x0 || y1 < y0) return out;
  }
  for (const [nx, ny] of families(fill)) {
    const tx = -ny;
    const ty = nx;
    // range of offsets d = p·n over the (visible part of the) bounding box
    const cs = [x0 * nx + y0 * ny, x1 * nx + y0 * ny, x0 * nx + y1 * ny, x1 * nx + y1 * ny];
    const dMin = Math.min(...cs);
    const dMax = Math.max(...cs);
    let count = 0;
    const hits: number[] = [];
    for (let d = Math.ceil(dMin / spacing) * spacing; d <= dMax && count < MAX_LINES; d += spacing, count++) {
      hits.length = 0;
      for (const r of rings) {
        const n = r.length / 2;
        for (let i = 0; i < n; i++) {
          const ax = r[i * 2];
          const ay = r[i * 2 + 1];
          const j = (i + 1) % n;
          const bx = r[j * 2];
          const by = r[j * 2 + 1];
          const da = ax * nx + ay * ny - d;
          const db = bx * nx + by * ny - d;
          // half-open crossing test: a vertex exactly on the line counts once
          if (da < 0 === db < 0) continue;
          const t = da / (da - db);
          hits.push((ax + (bx - ax) * t) * tx + (ay + (by - ay) * t) * ty);
        }
      }
      if (hits.length < 2) continue;
      hits.sort((a, b) => a - b);
      for (let k = 0; k + 1 < hits.length; k += 2) {
        out.push(d * nx + hits[k] * tx, d * ny + hits[k] * ty, d * nx + hits[k + 1] * tx, d * ny + hits[k + 1] * ty);
      }
    }
  }
  return out;
}

/** Triangles (two per segment) of segments with the given thickness. */
export function segmentQuads(segs: number[], thickness: number, out: number[]): void {
  const h = thickness / 2;
  for (let i = 0; i < segs.length; i += 4) {
    const ax = segs[i];
    const ay = segs[i + 1];
    const bx = segs[i + 2];
    const by = segs[i + 3];
    const len = Math.hypot(bx - ax, by - ay);
    if (len < 1e-9) continue;
    const ox = (-(by - ay) / len) * h;
    const oy = ((bx - ax) / len) * h;
    out.push(ax + ox, ay + oy, bx + ox, by + oy, bx - ox, by - oy, ax + ox, ay + oy, bx - ox, by - oy, ax - ox, ay - oy);
  }
}
